import {
  useEffect,
  useCallback,
  useMemo,
  useState,
  type ChangeEvent,
  type DragEvent,
} from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useAppDispatch, useAppSelector } from '../../Store/hooks';
import { resetStorageUpload } from '../../Store/StudentSlices/uploadStorageSlice';
import {
  resetCreateResource,
  uploadAndCreateResource,
  createResource,
} from '../../Store/StudentSlices/createResourceSlice';
import { resourceTypes, uploadConfig } from './constants';
import type { Resource, ResourceType, StudentOption } from './types';

type ResourceTypeValue = ResourceType | '';

type AddResourcePanelProps = {
  isOpen: boolean;
  coachId: string;
  students: StudentOption[];
  onClose: () => void;
  onResourceCreated: (resource: Resource) => void;
};

const AddResourcePanel = ({
  isOpen,
  coachId,
  students,
  onClose,
  onResourceCreated,
}: AddResourcePanelProps) => {
  const dispatch = useAppDispatch();
  const {
    loading: uploadLoading,
    error: uploadError,
    errorMessage: uploadErrorMessage,
  } = useAppSelector((state) => state.uploadStorage);
  const {
    loading: createLoading,
    error: createError,
    errorMessage: createErrorMessage,
  } = useAppSelector((state) => state.createResource);

  const [resourceType, setResourceType] = useState<ResourceTypeValue>('');
  const [resourceName, setResourceName] = useState('');
  const [resourceLink, setResourceLink] = useState('');
  const [fileName, setFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [formError, setFormError] = useState('');

  const isSubmitting = uploadLoading || createLoading;
  const isTypeSelected = Boolean(resourceType);
  const isLinkType = resourceType === 'Link';
  const currentUpload =
    !isLinkType && resourceType
      ? uploadConfig[resourceType as Exclude<ResourceType, 'Link'>]
      : null;

  const studentNameById = useMemo(() => {
    return new Map(students.map((student) => [student.id, student.name]));
  }, [students]);

  const selectedStudentOptions = useMemo(() => {
    if (selectedStudentIds.length === 0) {
      return [];
    }
    return students.filter((student) => selectedStudentIds.includes(student.id));
  }, [selectedStudentIds, students]);

  const selectedStudentNames = useMemo(() => {
    if (selectedStudentIds.length === 0) {
      return ['All'];
    }
    return selectedStudentIds.map(
      (studentId) => studentNameById.get(studentId) ?? 'Student'
    );
  }, [selectedStudentIds, studentNameById]);

  const shareSummary = useMemo(() => {
    if (selectedStudentIds.length === 0) {
      return '';
    }
    if (selectedStudentNames.length <= 2) {
      return selectedStudentNames.join(', ');
    }
    return `${selectedStudentNames[0]}, ${selectedStudentNames[1]} +${
      selectedStudentNames.length - 2
    } more`;
  }, [selectedStudentIds.length, selectedStudentNames]);

  const resetForm = useCallback(() => {
    setResourceType('');
    setResourceName('');
    setResourceLink('');
    setFileName('');
    setSelectedFile(null);
    setSelectedStudentIds([]);
    setIsDragging(false);
    setFormError('');
    dispatch(resetStorageUpload());
    dispatch(resetCreateResource());
  }, [dispatch]);

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setSelectedFile(file);
    setFileName(file.name);
    if (!resourceName.trim()) {
      setResourceName(file.name);
    }
  };

  const handleDragOver = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) {
      return;
    }
    setSelectedFile(file);
    setFileName(file.name);
    if (!resourceName.trim()) {
      setResourceName(file.name);
    }
  };

  const handleAddResource = async () => {
    dispatch(resetStorageUpload());
    dispatch(resetCreateResource());
    setFormError('');

    if (!resourceType) {
      setFormError('Select a resource type.');
      return;
    }

    const trimmedName = resourceName.trim();
    if (!trimmedName) {
      setFormError('Resource name is required.');
      return;
    }
    if (isLinkType && !resourceLink.trim()) {
      setFormError('Link is required.');
      return;
    }

    const sharedWithValue =
      selectedStudentIds.length === 0 ? ['All'] : selectedStudentNames;

    const addedOn = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    if (!isLinkType) {
      if (!selectedFile) {
        setFormError('Select a file to upload.');
        return;
      }
      const trimmedCoachId = coachId.trim();
      if (!trimmedCoachId) {
        setFormError('Coach ID is required.');
        return;
      }
      const selectedIdsForPayload =
        selectedStudentIds.length === 0 ? [] : selectedStudentIds;
      try {
        const extension = selectedFile.name.split('.').pop()?.toLowerCase();
        const inferredFileType =
          extension && extension !== selectedFile.name.toLowerCase()
            ? extension
            : (resourceType as ResourceType).toLowerCase();

        const result = await dispatch(
          uploadAndCreateResource({
            coachId: trimmedCoachId,
            file: selectedFile,
            fileType: inferredFileType,
            fileName: trimmedName,
            student_id: selectedIdsForPayload,
          })
        ).unwrap();

        onResourceCreated({
          id: `resource-${Date.now()}`,
          name: trimmedName,
          type: resourceType as ResourceType,
          sharedWith: sharedWithValue,
          addedOn,
          link: result.fileUrl,
        });

        handleClose();
      } catch (error) {
        setFormError(error instanceof Error ? error.message : 'Upload failed.');
      }
      return;
    }

    // For links, directly create the resource without file upload
    const trimmedCoachId = coachId.trim();
    if (!trimmedCoachId) {
      setFormError('Coach ID is required.');
      return;
    }
    const selectedIdsForPayload =
      selectedStudentIds.length === 0 ? [] : selectedStudentIds;
    try {
      await dispatch(
        createResource({
          coachId: trimmedCoachId,
          fileType: 'link',
          fileUrl: resourceLink.trim(),
          fileName: trimmedName,
          student_id: selectedIdsForPayload,
        })
      ).unwrap();

      onResourceCreated({
        id: `resource-${Date.now()}`,
        name: trimmedName,
        type: resourceType as ResourceType,
        sharedWith: sharedWithValue,
        addedOn,
        link: resourceLink.trim(),
      });

      handleClose();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Link creation failed.');
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <aside className="flex w-full  flex-col gap-6 rounded-[16px] border border-slate-100 bg-white px-5 py-5 shadow-sm xl:w-[320px] xl:min-w-[320px]">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-900">Add Resource</p>
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100"
          aria-label="Close"
          onClick={handleClose}
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex flex-col gap-6 border-t border-slate-100 pt-4 text-sm text-slate-700">
        <div className="flex flex-col gap-3 border-b border-slate-100 pb-4">
          <label className="text-sm font-semibold text-slate-900">
            Resource Type
          </label>
          <TextField
            select
            value={resourceType}
            size="small"
            disabled={isSubmitting}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) =>
                selected ? (
                  String(selected)
                ) : (
                  <span style={{ color: '#94A3B8' }}>Select Type</span>
                ),
              MenuProps: {
                PaperProps: {
                  sx: {
                    borderRadius: '10px',
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 16px 32px -20px rgba(15, 23, 42, 0.4)',
                    marginTop: '8px',
                    overflow: 'hidden',
                  },
                },
                MenuListProps: {
                  sx: {
                    padding: 0,
                  },
                },
              },
            }}
            onChange={(event) => {
              const nextType = event.target.value as ResourceTypeValue;
              setResourceType(nextType);
              setFileName('');
              setSelectedFile(null);
              if (nextType !== 'Link') {
                setResourceLink('');
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                backgroundColor: '#fff',
                height: '40px',
                paddingRight: '8px',
                width:"150px"
              },
              '& .MuiSelect-select': {
                padding: '10px 14px',
                fontSize: '14px',
                color: resourceType ? '#475569' : '#94A3B8',
              },
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: '#E2E8F0',
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: '#2563EB',
                },
              '& .MuiSvgIcon-root': {
                color: '#94A3B8',
              },
            }}
          >
            {resourceTypes.map((type) => (
              <MenuItem
                key={type}
                value={type}
                sx={{
                  padding: '14px 16px',
                  fontSize: '14px',
                  color: '#64748B',
                  borderBottom: '1px solid #F1F5F9',
                  '&:last-of-type': {
                    borderBottom: 'none',
                  },
                }}
              >
                {type}
              </MenuItem>
            ))}
          </TextField>
        </div>

       

        {isTypeSelected ? (
          <>
            <div>
              <label className="text-sm font-semibold text-slate-900">
                Resource Name
              </label>
              <input
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none"
                placeholder="Enter resource name"
                value={resourceName}
                onChange={(event) => setResourceName(event.target.value)}
              />
            </div>

            {isLinkType ? (
              <div>
                <label className="text-sm font-semibold text-slate-900">Link</label>
                <input
                  className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none"
                  placeholder="Paste your link here"
                  value={resourceLink}
                  onChange={(event) => setResourceLink(event.target.value)}
                />
              </div>
            ) : (
              <div
                className={`rounded-lg border border-dashed px-4 py-6 text-center text-xs text-slate-400 ${
                  isDragging
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-slate-200 bg-slate-50'
                }`}
              >
                <label
                  className="flex cursor-pointer flex-col items-center gap-2 text-sm font-semibold text-slate-600"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <svg
                    className="h-6 w-6 text-slate-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <path d="M7 10l5-5 5 5" />
                    <path d="M12 15V5" />
                  </svg>
                  <span className="block max-w-[250px] truncate text-center" title={fileName || currentUpload?.label}>
                    {fileName ? fileName : currentUpload?.label}
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    accept={currentUpload?.accept}
                    onChange={handleFileChange}
                  />
                </label>
                <p className="mt-2 text-[11px] text-slate-400">
                  {currentUpload?.hint}
                </p>
              </div>
            )}
          </>
        ) : null}
         <div className="flex flex-col gap-3 border-b border-slate-100 pb-4">
          <label className="text-sm font-semibold text-slate-900">Share with</label>
          <Autocomplete
            multiple
            options={students}
            value={selectedStudentOptions}
            disableCloseOnSelect
            disabled={isSubmitting}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name}
            onChange={(_, newValue) => {
              const ids = newValue.map((option) => option.id);
              setSelectedStudentIds(ids);
            }}
            renderTags={() =>
              shareSummary
                ? [
                    <span
                      key="summary"
                      className="text-sm text-slate-700"
                    >
                      {shareSummary}
                    </span>,
                  ]
                : []
            }
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox
                  checked={selected}
                  sx={{
                    marginRight: '8px',
                    color: '#CBD5F5',
                    '&.Mui-checked': {
                      color: '#2563EB',
                    },
                  }}
                />
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={shareSummary ? '' : 'Select Student'}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#fff',
                    minHeight: '44px',
                    paddingRight: '8px',
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '10px 14px',
                    fontSize: '14px',
                    color: '#475569',
                  },
                  '& .MuiOutlinedInput-input::placeholder': {
                    color: '#94A3B8',
                    opacity: 1,
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E2E8F0',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                    {
                      borderColor: '#2563EB',
                    },
                  '& .MuiSvgIcon-root': {
                    color: '#94A3B8',
                  },
                }}
              />
            )}
            slotProps={{
              popper: {
                sx: {
                  '&[data-popper-placement*="bottom"] .MuiAutocomplete-paper': {
                    marginTop: '8px',
                  },
                  '&[data-popper-placement*="top"] .MuiAutocomplete-paper': {
                    marginBottom: '8px',
                  },
                },
              },
            }}
            sx={{
              '& .MuiAutocomplete-paper': {
                borderRadius: '10px',
                border: '1px solid #E2E8F0',
                boxShadow: '0 16px 32px -20px rgba(15, 23, 42, 0.4)',
                overflow: 'hidden',
              },
              '& .MuiAutocomplete-listbox': {
                padding: 0,
              },
              '& .MuiAutocomplete-option': {
                padding: '14px 16px',
                fontSize: '14px',
                color: '#64748B',
                borderBottom: '1px solid #F1F5F9',
                '&[aria-selected="true"]': {
                  backgroundColor: '#F1F5F9',
                },
                '&.Mui-focused': {
                  backgroundColor: '#F8FAFC',
                },
                '&:last-of-type': {
                  borderBottom: 'none',
                },
              },
            }}
          />
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            onClick={handleAddResource}
            disabled={isSubmitting}
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 3v12" />
              <path d="M6 9h12" />
              <path d="M5 21h14a2 2 0 0 0 2-2V7" />
            </svg>
            {uploadLoading
              ? 'Uploading...'
              : createLoading
              ? 'Saving...'
              : 'Add'}
          </button>
          <button
            type="button"
            className="flex flex-1 items-center justify-center rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
        {formError || uploadError || createError ? (
          <p className="text-[11px] text-rose-500">
            {formError ||
              uploadErrorMessage ||
              createErrorMessage ||
              'Upload failed. Please try again.'}
          </p>
        ) : null}
      </div>
    </aside>
  );
};

export default AddResourcePanel;
